import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import AdminLayout from '@/layouts/AdminLayout';
import { useAuthState } from '@/hooks/useAuthState';
import { BannerService, Banner } from '@/services/bannerService';
import { getBannerImageUrl } from '@/config/api';
import { 
  Image, 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  ArrowLeft,
  MoreHorizontal,
  CheckCircle,
  XCircle,
  Ban,
  Power,
  Calendar,
  Upload,
  X,
  Home,
  Star,
  Heart,
  Sparkles,
  ArrowRight,
  Gift,
  Trophy,
  Award,
  Zap,
  Flame,
  Sun,
  Moon,
  Camera,
  MapPin,
  ShoppingBag,
  Coffee,
  Music,
  Gamepad2,
  Book,
  Palette,
  Wand2,
  Rocket,
  Target,
  TrendingUp,
  Bell,
  Mail,
  Phone,
  MessageCircle,
  Users,
  Clock,
  Tag,
  Flag,
  AlertCircle,
  Info,
  HelpCircle,
  Lock,
  Unlock,
  EyeOff,
  Filter,
  Settings,
  Menu,
  Minus,
  Save,
  Download,
  Share,
  Copy,
  ExternalLink,
  File,
  Folder,
  Database,
  Server,
  Cloud,
  Wifi,
  Bluetooth,
  Battery,
  Volume,
  Mic,
  Video,
  Headphones,
  Monitor,
  Smartphone,
  Tablet,
  Laptop,
  Printer,
  Scanner,
  Keyboard,
  Mouse,
  HardDrive,
  Cpu,
  Memory,
  Network,
  Globe,
  Map,
  Navigation,
  Compass,
  Building,
  Hotel,
  Car,
  Plane,
  Train,
  Ship,
  Bike,
  Bus,
  Taxi,
  Truck,
  Package,
  Box,
  ShoppingCart,
  CreditCard,
  Wallet,
  Banknote,
  Coins,
  DollarSign,
  Euro,
  Pound,
  Yen,
  Bitcoin,
  TrendingDown,
  BarChart,
  LineChart,
  PieChart,
  Activity,
  Pulse,
  Heartbeat,
  Thermometer,
  Droplet,
  Wind,
  CloudRain,
  CloudSnow,
  CloudLightning,
  Sunrise,
  Sunset,
  Rainbow,
  Umbrella,
  Shield,
  Sword,
  Axe,
  Bow,
  Crosshair,
  Bomb,
  Fire,
  Lightning,
  Bolt,
  Energy,
  Atom,
  Molecule,
  Dna,
  Microscope,
  Telescope,
  Satellite,
  Space,
  Planet,
  Galaxy,
  Comet,
  Meteor,
  Asteroid,
  BlackHole,
  Nebula,
  StarCluster,
  Constellation,
  MilkyWay,
  SolarSystem,
  Earth,
  Mars,
  Jupiter,
  Saturn,
  Uranus,
  Neptune,
  Pluto,
  Mercury,
  Venus
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Lista de ícones disponíveis
const availableIcons = [
  { name: 'Nenhum', value: '', icon: X },
  { name: 'Home', value: 'Home', icon: Home },
  { name: 'Star', value: 'Star', icon: Star },
  { name: 'Heart', value: 'Heart', icon: Heart },
  { name: 'Sparkles', value: 'Sparkles', icon: Sparkles },
  { name: 'ArrowRight', value: 'ArrowRight', icon: ArrowRight },
  { name: 'Gift', value: 'Gift', icon: Gift },
  { name: 'Trophy', value: 'Trophy', icon: Trophy },
  { name: 'Award', value: 'Award', icon: Award },
  { name: 'Zap', value: 'Zap', icon: Zap },
  { name: 'Flame', value: 'Flame', icon: Flame },
  { name: 'Sun', value: 'Sun', icon: Sun },
  { name: 'Moon', value: 'Moon', icon: Moon },
  { name: 'Camera', value: 'Camera', icon: Camera },
  { name: 'MapPin', value: 'MapPin', icon: MapPin },
  { name: 'ShoppingBag', value: 'ShoppingBag', icon: ShoppingBag },
  { name: 'Coffee', value: 'Coffee', icon: Coffee },
  { name: 'Music', value: 'Music', icon: Music },
  { name: 'Gamepad2', value: 'Gamepad2', icon: Gamepad2 },
  { name: 'Book', value: 'Book', icon: Book },
  { name: 'Palette', value: 'Palette', icon: Palette },
  { name: 'Wand2', value: 'Wand2', icon: Wand2 },
  { name: 'Rocket', value: 'Rocket', icon: Rocket },
  { name: 'Target', value: 'Target', icon: Target },
  { name: 'TrendingUp', value: 'TrendingUp', icon: TrendingUp },
  { name: 'Bell', value: 'Bell', icon: Bell },
  { name: 'Mail', value: 'Mail', icon: Mail },
  { name: 'Phone', value: 'Phone', icon: Phone },
  { name: 'MessageCircle', value: 'MessageCircle', icon: MessageCircle },
  { name: 'Users', value: 'Users', icon: Users },
  { name: 'Calendar', value: 'Calendar', icon: Calendar },
  { name: 'Clock', value: 'Clock', icon: Clock },
  { name: 'Tag', value: 'Tag', icon: Tag },
  { name: 'Flag', value: 'Flag', icon: Flag },
  { name: 'CheckCircle', value: 'CheckCircle', icon: CheckCircle },
  { name: 'AlertCircle', value: 'AlertCircle', icon: AlertCircle },
  { name: 'Info', value: 'Info', icon: Info },
  { name: 'HelpCircle', value: 'HelpCircle', icon: HelpCircle },
  { name: 'Lock', value: 'Lock', icon: Lock },
  { name: 'Unlock', value: 'Unlock', icon: Unlock },
  { name: 'Eye', value: 'Eye', icon: Eye },
  { name: 'Search', value: 'Search', icon: Search },
  { name: 'Filter', value: 'Filter', icon: Filter },
  { name: 'Settings', value: 'Settings', icon: Settings },
  { name: 'Globe', value: 'Globe', icon: Globe },
  { name: 'Map', value: 'Map', icon: Map },
  { name: 'Navigation', value: 'Navigation', icon: Navigation },
  { name: 'Compass', value: 'Compass', icon: Compass },
  { name: 'Building', value: 'Building', icon: Building },
  { name: 'Hotel', value: 'Hotel', icon: Hotel },
  { name: 'Car', value: 'Car', icon: Car },
  { name: 'Plane', value: 'Plane', icon: Plane },
  { name: 'Train', value: 'Train', icon: Train },
  { name: 'Ship', value: 'Ship', icon: Ship },
  { name: 'Bike', value: 'Bike', icon: Bike },
  { name: 'ShoppingCart', value: 'ShoppingCart', icon: ShoppingCart },
  { name: 'CreditCard', value: 'CreditCard', icon: CreditCard },
  { name: 'Wallet', value: 'Wallet', icon: Wallet },
  { name: 'DollarSign', value: 'DollarSign', icon: DollarSign },
  { name: 'TrendingDown', value: 'TrendingDown', icon: TrendingDown },
  { name: 'BarChart', value: 'BarChart', icon: BarChart },
  { name: 'Activity', value: 'Activity', icon: Activity },
  { name: 'Shield', value: 'Shield', icon: Shield },
  { name: 'Umbrella', value: 'Umbrella', icon: Umbrella },
  { name: 'Cloud', value: 'Cloud', icon: Cloud },
  { name: 'Sunrise', value: 'Sunrise', icon: Sunrise },
  { name: 'Sunset', value: 'Sunset', icon: Sunset },
  { name: 'Rainbow', value: 'Rainbow', icon: Rainbow },
];

// Função para obter o componente de ícone pelo nome
const getIconComponent = (iconName: string | null | undefined) => {
  if (!iconName) return null;
  const iconData = availableIcons.find(i => i.value === iconName);
  return iconData ? iconData.icon : null;
};

const AdminBanners = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated, userType, userName, userPhoto, userId, loading: authLoading } = useAuthState();
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterType, setFilterType] = useState('todos');
  const [filterStatus, setFilterStatus] = useState('todos');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  
  // Estados para modal de criação/edição
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState<Banner | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'principal' as 'principal' | 'destaque' | 'secundario',
    image_path: '',
    background_color: '',
    icon: '',
    title: '',
    subtitle: '',
    text: '',
    link: '',
    is_fixed_image: false, // Banner com imagem fixa (sem textos)
    situacao: 'cadastrado' as 'cadastrado' | 'publicado',
    status: 'ativo' as 'ativo' | 'inativo',
    start_date: '',
    end_date: '',
  });
  const [imagePreview, setImagePreview] = useState<string>('');
  const [uploadingImage, setUploadingImage] = useState(false);

  // Verificar se é admin
  useEffect(() => {
    if (!authLoading && (!isAuthenticated || userType !== 'admin')) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, userType, authLoading, navigate]);

  useEffect(() => {
    loadBanners();
  }, [currentPage, itemsPerPage, searchTerm, filterType, filterStatus]);

  const loadBanners = async () => {
    try {
      setLoading(true);
      const allBanners = await BannerService.getAllBanners();

      // Aplicar filtros
      let filteredBanners = allBanners;
      
      if (searchTerm) {
        filteredBanners = filteredBanners.filter(banner => 
          banner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (banner.text && banner.text.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }

      if (filterType !== 'todos') {
        filteredBanners = filteredBanners.filter(banner => banner.type === filterType);
      }

      if (filterStatus !== 'todos') {
        if (filterStatus === 'cadastrado') {
          filteredBanners = filteredBanners.filter(banner => banner.situacao === 'cadastrado');
        } else if (filterStatus === 'publicado') {
          filteredBanners = filteredBanners.filter(banner => banner.situacao === 'publicado');
        } else if (filterStatus === 'ativo') {
          filteredBanners = filteredBanners.filter(banner => banner.status === 'ativo');
        } else if (filterStatus === 'inativo') {
          filteredBanners = filteredBanners.filter(banner => banner.status === 'inativo');
        }
      }

      const total = filteredBanners.length;
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedBanners = filteredBanners.slice(startIndex, endIndex);

      setBanners(paginatedBanners);
      setTotalItems(total);
    } catch (error: any) {
      console.error('Erro ao carregar banners:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao carregar banners',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleOpenModal = (banner?: Banner) => {
    if (banner) {
      setEditingBanner(banner);
      // Determinar se é imagem fixa (sem título e subtítulo)
      // Verificar se ambos são null ou string vazia
      const isFixedImage = (!banner.title || banner.title.trim() === '') && 
                          (!banner.subtitle || banner.subtitle.trim() === '');
      
      console.log('📂 Carregando banner para edição:', {
        id: banner.id,
        title: banner.title,
        subtitle: banner.subtitle,
        title_is_null: banner.title === null,
        subtitle_is_null: banner.subtitle === null,
        isFixedImage
      });
      
      setFormData({
        name: banner.name,
        type: banner.type,
        image_path: banner.image_path,
        background_color: banner.background_color || '',
        icon: banner.icon || '',
        title: banner.title || '',
        subtitle: banner.subtitle || '',
        text: banner.text || '',
        link: banner.link || '',
        is_fixed_image: isFixedImage,
        situacao: banner.situacao,
        status: banner.status,
        start_date: banner.start_date ? format(new Date(banner.start_date), "yyyy-MM-dd'T'HH:mm") : '',
        end_date: banner.end_date ? format(new Date(banner.end_date), "yyyy-MM-dd'T'HH:mm") : '',
      });
      setImagePreview(getBannerImageUrl(banner.image_path));
    } else {
      setEditingBanner(null);
      setFormData({
        name: '',
        type: 'principal',
        image_path: '',
        title: '',
        subtitle: '',
        text: '',
        link: '',
        situacao: 'cadastrado',
        status: 'ativo',
        start_date: '',
        end_date: '',
      });
      setImagePreview('');
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingBanner(null);
      setFormData({
        name: '',
        type: 'principal',
        image_path: '',
        background_color: '',
        icon: '',
        title: '',
        subtitle: '',
        text: '',
        link: '',
        is_fixed_image: false,
        situacao: 'cadastrado',
        status: 'ativo',
        start_date: '',
        end_date: '',
      });
    setImagePreview('');
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingImage(true);
      const result = await BannerService.uploadBannerImage(file);
      setFormData({ ...formData, image_path: result.path });
      setImagePreview(getBannerImageUrl(result.path));
      toast({
        title: 'Sucesso',
        description: 'Imagem enviada com sucesso',
      });
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao fazer upload da imagem',
        variant: 'destructive',
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar campos obrigatórios
    if (!formData.name || !formData.link) {
      toast({
        title: 'Erro',
        description: 'Nome e URL são obrigatórios',
        variant: 'destructive',
      });
      return;
    }
    
    // Imagem é obrigatória apenas se não estiver editando ou se não houver imagem atual
    if (!formData.image_path && (!editingBanner || !editingBanner.image_path)) {
      toast({
        title: 'Erro',
        description: 'Imagem é obrigatória',
        variant: 'destructive',
      });
      return;
    }
    
    // Se for imagem fixa, limpar título e subtítulo
    // IMPORTANTE: Sempre enviar title e subtitle explicitamente para garantir que sejam atualizados
    // O backend verifica se !== undefined, então sempre devemos enviar (mesmo que seja null)
    const titleValue = formData.is_fixed_image 
      ? null 
      : (formData.title && formData.title.trim() ? formData.title.trim() : null);
    
    const subtitleValue = formData.is_fixed_image 
      ? null 
      : (formData.subtitle && formData.subtitle.trim() ? formData.subtitle.trim() : null);
    
    // IMPORTANTE: Sempre incluir title e subtitle no objeto, mesmo que sejam null
    // Isso garante que o backend os atualize corretamente
    const bannerData: any = {
      name: formData.name,
      type: formData.type,
      title: titleValue, // SEMPRE enviar (null ou string) - nunca undefined
      subtitle: subtitleValue, // SEMPRE enviar (null ou string) - nunca undefined
      text: formData.text || null,
      link: formData.link,
      background_color: formData.background_color || null,
      icon: formData.icon || null,
      situacao: formData.situacao,
      status: formData.status,
      start_date: formData.start_date || null,
      end_date: formData.end_date || null,
    };
    
    // Incluir image_path apenas se houver uma nova imagem selecionada
    // Se estiver editando e não selecionou nova imagem, não incluir image_path (undefined)
    // para que o backend mantenha a imagem atual
    if (formData.image_path) {
      bannerData.image_path = formData.image_path;
    }
    // Se não houver image_path e estiver editando, não incluir no objeto (deixar undefined)

    console.log('💾 Salvando banner:', {
      editingBanner: editingBanner?.id,
      is_fixed_image: formData.is_fixed_image,
      formData_title: formData.title,
      formData_subtitle: formData.subtitle,
      titleValue,
      subtitleValue,
      bannerData,
      bannerDataKeys: Object.keys(bannerData)
    });

    try {
      if (editingBanner) {
        console.log('🔄 Atualizando banner ID:', editingBanner.id);
        const updatedBanner = await BannerService.updateBanner(editingBanner.id, bannerData);
        console.log('✅ Banner atualizado:', updatedBanner);
        console.log('📋 Banner atualizado - title:', updatedBanner.title, 'subtitle:', updatedBanner.subtitle);
        toast({
          title: 'Sucesso',
          description: 'Banner atualizado com sucesso',
        });
      } else {
        console.log('➕ Criando novo banner');
        const newBanner = await BannerService.createBanner(bannerData);
        console.log('✅ Banner criado:', newBanner);
        toast({
          title: 'Sucesso',
          description: 'Banner criado com sucesso',
        });
      }

      handleCloseModal();
      // Recarregar banners para garantir que a lista esteja atualizada
      await loadBanners();
    } catch (error: any) {
      console.error('Erro ao salvar banner:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao salvar banner',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Tem certeza que deseja deletar este banner?')) return;

    try {
      await BannerService.deleteBanner(id);
      toast({
        title: 'Sucesso',
        description: 'Banner deletado com sucesso',
      });
      loadBanners();
    } catch (error: any) {
      console.error('Erro ao deletar banner:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao deletar banner',
        variant: 'destructive',
      });
    }
  };

  const handleToggleStatus = async (banner: Banner) => {
    try {
      await BannerService.updateBanner(banner.id, {
        situacao: banner.situacao === 'cadastrado' ? 'publicado' : 'cadastrado',
      });
      toast({
        title: 'Sucesso',
        description: `Banner ${banner.situacao === 'cadastrado' ? 'publicado' : 'despublicado'} com sucesso`,
      });
      loadBanners();
    } catch (error: any) {
      console.error('Erro ao alterar situação:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao alterar situação',
        variant: 'destructive',
      });
    }
  };

  const handleToggleActive = async (banner: Banner) => {
    try {
      await BannerService.updateBanner(banner.id, {
        status: banner.status === 'ativo' ? 'inativo' : 'ativo',
      });
      toast({
        title: 'Sucesso',
        description: `Banner ${banner.status === 'ativo' ? 'desativado' : 'ativado'} com sucesso`,
      });
      loadBanners();
    } catch (error: any) {
      console.error('Erro ao alterar status:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao alterar status',
        variant: 'destructive',
      });
    }
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      principal: 'Banner Principal',
      destaque: 'Destaque',
      secundario: 'Banner Secundário',
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando banners...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/admin/dashboard')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar
              </Button>
              <div>
                <h1 className="text-2xl font-bold">Gerenciar Banners</h1>
                <p className="text-muted-foreground">
                  Cadastre, edite e gerencie banners do site
                </p>
              </div>
            </div>
            <Button onClick={() => handleOpenModal()}>
              <Plus className="h-4 w-4 mr-2" />
              Novo Banner
            </Button>
          </div>

          {/* Busca e Filtros */}
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex-1">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        placeholder="Buscar por nome ou texto..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyPress={handleKeyPress}
                        className="pl-10"
                      />
                    </div>
                    <Button onClick={handleSearch} variant="outline" className="px-4">
                      <Search className="h-4 w-4" />
                    </Button>
                    {searchTerm && (
                      <Button onClick={handleClearSearch} variant="outline" className="px-4">
                        Limpar
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Tipo:</span>
                  <Select value={filterType} onValueChange={(value) => {
                    setFilterType(value);
                    setCurrentPage(1);
                  }}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      <SelectItem value="principal">Principal</SelectItem>
                      <SelectItem value="destaque">Destaque</SelectItem>
                      <SelectItem value="secundario">Secundário</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Status:</span>
                  <Select value={filterStatus} onValueChange={(value) => {
                    setFilterStatus(value);
                    setCurrentPage(1);
                  }}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      <SelectItem value="cadastrado">Cadastrado</SelectItem>
                      <SelectItem value="publicado">Publicado</SelectItem>
                      <SelectItem value="ativo">Ativo</SelectItem>
                      <SelectItem value="inativo">Inativo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Itens por página:</span>
                  <Select value={itemsPerPage.toString()} onValueChange={(value) => handleItemsPerPageChange(parseInt(value))}>
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tabela de Banners */}
          <Card>
            <CardHeader>
              <CardTitle>Banners ({totalItems})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Imagem</TableHead>
                      <TableHead>Nome</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Situação</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Cliques</TableHead>
                      <TableHead>Impressões</TableHead>
                      <TableHead>Período</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {banners.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                          Nenhum banner encontrado
                        </TableCell>
                      </TableRow>
                    ) : (
                      banners.map((banner) => (
                        <TableRow key={banner.id}>
                          <TableCell>
                            <img
                              src={getBannerImageUrl(banner.image_path)}
                              alt={banner.name}
                              className="w-20 h-12 object-cover rounded"
                            />
                          </TableCell>
                          <TableCell className="font-medium">{banner.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{getTypeLabel(banner.type)}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={banner.situacao === 'publicado' ? 'default' : 'secondary'}>
                              {banner.situacao === 'publicado' ? 'Publicado' : 'Cadastrado'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={banner.status === 'ativo' ? 'default' : 'secondary'}>
                              {banner.status === 'ativo' ? 'Ativo' : 'Inativo'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center font-medium">
                            {banner.clicks || 0}
                          </TableCell>
                          <TableCell className="text-center font-medium">
                            {banner.views || 0}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {banner.start_date ? (
                              <>
                                {format(new Date(banner.start_date), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                                {banner.end_date && (
                                  <> até {format(new Date(banner.end_date), "dd/MM/yyyy HH:mm", { locale: ptBR })}</>
                                )}
                              </>
                            ) : (
                              'Sem limite'
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleOpenModal(banner)}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Editar
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleToggleStatus(banner)}>
                                  {banner.situacao === 'cadastrado' ? (
                                    <>
                                      <CheckCircle className="h-4 w-4 mr-2" />
                                      Publicar
                                    </>
                                  ) : (
                                    <>
                                      <XCircle className="h-4 w-4 mr-2" />
                                      Despublicar
                                    </>
                                  )}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleToggleActive(banner)}>
                                  {banner.status === 'ativo' ? (
                                    <>
                                      <Ban className="h-4 w-4 mr-2" />
                                      Desativar
                                    </>
                                  ) : (
                                    <>
                                      <Power className="h-4 w-4 mr-2" />
                                      Ativar
                                    </>
                                  )}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDelete(banner.id)} className="text-red-600">
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Deletar
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Paginação */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-muted-foreground">
                    Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, totalItems)} de {totalItems} banners
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Anterior
                    </Button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <Button
                            key={pageNum}
                            variant={currentPage === pageNum ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(pageNum)}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Próxima
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modal de Criação/Edição */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingBanner ? 'Editar Banner' : 'Novo Banner'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              {/* Coluna Esquerda - Formulário */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Nome do Banner *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="Ex: Banner Principal"
                  />
                </div>

                <div>
                  <Label htmlFor="type">Tipo *</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value: 'principal' | 'destaque' | 'secundario') =>
                      setFormData({ ...formData, type: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="principal">Banner Principal</SelectItem>
                      <SelectItem value="destaque">Destaque</SelectItem>
                      <SelectItem value="secundario">Banner Secundário</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="image">
                    Imagem {!editingBanner || !formData.image_path ? '*' : ''}
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="image"
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                      className="flex-1"
                      required={!editingBanner || !formData.image_path}
                    />
                    {uploadingImage && (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                    )}
                  </div>
                  {formData.image_path && (
                    <div className="mt-2">
                      <p className="text-xs text-muted-foreground mb-1">
                        Imagem atual: {formData.image_path}
                      </p>
                      {imagePreview && (
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          className="w-full max-w-xs h-24 object-cover rounded border"
                        />
                      )}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {formData.type === 'principal' && 'Tamanho recomendado: 1920x480px (largura x altura)'}
                    {formData.type === 'destaque' && 'Tamanho recomendado: 1200x100px (largura x altura)'}
                    {formData.type === 'secundario' && 'Tamanho recomendado: 1200x100px (largura x altura)'}
                    {editingBanner && formData.image_path && ' • Deixe vazio para manter a imagem atual'}
                  </p>
                </div>

                <div>
                  <Label htmlFor="background_color">Cor de Fundo</Label>
                  <div className="flex gap-2 items-center">
                    <Input
                      id="background_color"
                      type="color"
                      value={formData.background_color || '#FFFFFF'}
                      onChange={(e) => {
                        const color = e.target.value.toUpperCase();
                        setFormData({ ...formData, background_color: color });
                      }}
                      className="w-20 h-10 cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={formData.background_color || ''}
                      onChange={(e) => {
                        let value = e.target.value.toUpperCase().replace(/[^0-9A-F]/g, '');
                        if (value.length > 0 && !value.startsWith('#')) {
                          value = '#' + value;
                        }
                        if (value.length > 7) {
                          value = value.substring(0, 7);
                        }
                        setFormData({ ...formData, background_color: value || '' });
                      }}
                      placeholder="#FFFFFF"
                      maxLength={7}
                      pattern="^#[0-9A-Fa-f]{6}$"
                      className="flex-1"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Selecione uma cor ou digite o código hexadecimal (ex: #FFFFFF)
                  </p>
                </div>

                <div>
                  <Label htmlFor="icon">Ícone</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full justify-start"
                      >
                        {formData.icon ? (
                          <>
                            {(() => {
                              const IconComponent = getIconComponent(formData.icon);
                              return IconComponent ? (
                                <>
                                  <IconComponent className="h-4 w-4 mr-2" />
                                  {availableIcons.find(i => i.value === formData.icon)?.name}
                                </>
                              ) : (
                                formData.icon
                              );
                            })()}
                          </>
                        ) : (
                          <>Selecione um ícone (opcional)</>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-4">
                      <div className="grid grid-cols-6 gap-2 max-h-64 overflow-y-auto">
                        {availableIcons.map((iconData) => {
                          const IconComponent = iconData.icon;
                          return (
                            <button
                              key={iconData.value}
                              type="button"
                              onClick={() => {
                                setFormData({ ...formData, icon: iconData.value });
                              }}
                              className={`flex flex-col items-center justify-center p-2 rounded border transition-colors ${
                                formData.icon === iconData.value
                                  ? 'bg-primary text-primary-foreground border-primary'
                                  : 'hover:bg-accent border-border'
                              }`}
                              title={iconData.name}
                            >
                              <IconComponent className="h-5 w-5 mb-1" />
                              <span className="text-xs truncate w-full text-center">
                                {iconData.name}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-muted-foreground mt-1">
                    Selecione um ícone para exibir no banner (opcional)
                  </p>
                </div>

                <div>
                  <Label htmlFor="title">Título</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Título do banner (opcional)"
                    disabled={formData.is_fixed_image}
                  />
                  {formData.is_fixed_image && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Desabilitado para banners com imagem fixa
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="subtitle">Subtítulo</Label>
                  <Input
                    id="subtitle"
                    value={formData.subtitle}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                    placeholder="Subtítulo do banner (opcional)"
                    disabled={formData.is_fixed_image}
                  />
                  {formData.is_fixed_image && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Desabilitado para banners com imagem fixa
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="text">Texto</Label>
                  <Textarea
                    id="text"
                    value={formData.text}
                    onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                    placeholder="Texto do banner (opcional)"
                    rows={3}
                  />
                </div>

                <div>
                  <Label htmlFor="link">URL *</Label>
                  <Input
                    id="link"
                    type="url"
                    value={formData.link}
                    onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                    placeholder="https://exemplo.com ou /busca"
                    required
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    URL absoluta (https://) ou relativa (/busca)
                  </p>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_fixed_image"
                    checked={!!formData.is_fixed_image}
                    onCheckedChange={(checked) => {
                      console.log('🔄 Switch alterado:', checked);
                      console.log('📋 Estado anterior is_fixed_image:', formData.is_fixed_image);
                      console.log('📋 Estado anterior title:', formData.title);
                      console.log('📋 Estado anterior subtitle:', formData.subtitle);
                      
                      // Usar função de atualização para garantir que o estado seja atualizado corretamente
                      setFormData(prev => {
                        const newData = {
                          ...prev,
                          is_fixed_image: checked,
                          // Se marcar como imagem fixa, limpar título e subtítulo
                          // Se desmarcar, manter os valores atuais (podem estar vazios)
                          title: checked ? '' : (prev.title || ''),
                          subtitle: checked ? '' : (prev.subtitle || '')
                        };
                        
                        console.log('📋 Novo estado is_fixed_image:', newData.is_fixed_image);
                        console.log('📋 Novo estado title:', newData.title);
                        console.log('📋 Novo estado subtitle:', newData.subtitle);
                        
                        return newData;
                      });
                    }}
                  />
                  <Label htmlFor="is_fixed_image" className="cursor-pointer">
                    Banner com imagem fixa (sem textos)
                  </Label>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="situacao">Situação</Label>
                    <Select
                      value={formData.situacao}
                      onValueChange={(value: 'cadastrado' | 'publicado') =>
                        setFormData({ ...formData, situacao: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cadastrado">Cadastrado</SelectItem>
                        <SelectItem value="publicado">Publicado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="status">Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value: 'ativo' | 'inativo') =>
                        setFormData({ ...formData, status: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ativo">Ativo</SelectItem>
                        <SelectItem value="inativo">Inativo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="start_date">Data de Início</Label>
                  <Input
                    id="start_date"
                    type="datetime-local"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Deixe vazio para sem limite de início
                  </p>
                </div>

                <div>
                  <Label htmlFor="end_date">Data de Fim</Label>
                  <Input
                    id="end_date"
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    min={formData.start_date || undefined}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Deixe vazio para sem limite de fim
                  </p>
                </div>
              </div>

              {/* Coluna Direita - Preview */}
              <div className="space-y-4">
                <div>
                  <Label>Preview</Label>
                  <Card className="mt-2">
                    <CardContent className="p-4">
                      {imagePreview ? (
                        <div className="space-y-4">
                          <div className="relative w-full aspect-video bg-muted rounded-lg overflow-hidden">
                            <img
                              src={imagePreview}
                              alt="Preview"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          {formData.title && (
                            <div className="text-center">
                              <h3 className="text-xl font-bold">{formData.title}</h3>
                            </div>
                          )}
                          {formData.subtitle && (
                            <div className="text-center">
                              <p className="text-sm text-muted-foreground">{formData.subtitle}</p>
                            </div>
                          )}
                          {formData.text && (
                            <div className="text-center">
                              <p className="text-lg font-semibold">{formData.text}</p>
                            </div>
                          )}
                          {formData.link && (
                            <div className="text-center">
                              <a
                                href={formData.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:underline text-sm"
                              >
                                {formData.link}
                              </a>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-48 text-muted-foreground">
                          <div className="text-center">
                            <Image className="h-12 w-12 mx-auto mb-2 opacity-50" />
                            <p>Nenhuma imagem selecionada</p>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={handleCloseModal}>
                Cancelar
              </Button>
              <Button type="submit" disabled={uploadingImage}>
                {editingBanner ? 'Atualizar' : 'Criar'} Banner
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminBanners;

